import React from "react";
import PropTypes from "prop-types";
import { Form } from "@domestika-dev/prudencio/dist/atoms/Form";
import { useIntl } from "react-intl";

import { errorsMessageUploaderImageSingle } from "../utils";
import { MAX_IMAGE_CAPTION_LENGTH } from "../config";

const FormContentItem = ({ item, properties, errors }) => {
  const intl = useIntl();

  const {
    humanizedItemType: type,
    id,
    caption,
    contentId,
    description,
    url,
    embedUrl,
    embedHtml,
  } = item;
  const {
    onImagesLoad,
    validate,
    isImageLoading,
    onChange,
    onSetEmbedContent,
    isEmbedContentLoading,
  } = properties;

  if (!type) {
    return null;
  }

  let contentItem = null;
  let hasError = errors.length > 0;
  let errorMessage = hasError && errors.join(", ");

  switch (type) {
    case "s3image":
      contentItem = (
        <div data-label="project-uploader-image-content-item">
          <Form.Uploader
            maxUploadSizeMessage={intl.formatMessage(
              { id: "projects.new.image_upload.input.hint" },
              { size: 10 }
            )}
            descriptionMessage={intl.formatMessage({
              id: "projects.new.image_upload.input.body.singular",
            })}
            progressMessage={intl.formatMessage({ id: "projects.new.image_upload.input.loading" })}
            progressNumber={95}
            id={contentId || id}
            onImagesLoad={onImagesLoad}
            name={`contentItemUploaderSingle-${contentId || id}`}
            size="small"
            maxNumFiles={1}
            validate={validate}
            key={contentId || id}
            loading={isImageLoading}
            errors={errorsMessageUploaderImageSingle}
          />
        </div>
      );
      break;
    case "image":
      contentItem = (
        <div data-label="project-image-content-item">
          <Form.ContentImage
            id={contentId || id}
            name={contentId || id}
            key={contentId || id}
            src={url}
            onChange={e => {
              onChange(contentId || id, "caption", e.target.value);
            }}
            defaultValue={caption}
            hasError={hasError}
            errorMessage={errorMessage}
            placeholderMessage={intl.formatMessage({ id: "projects.new.caption.placeholder" })}
            maxLength={MAX_IMAGE_CAPTION_LENGTH}
          />
        </div>
      );
      break;
    case "embed":
      contentItem = (
        <div data-label="project-video-content-item">
          <Form.ContentVideo
            id={id || contentId}
            name={id || contentId}
            onChange={e => {
              onChange(contentId || id, "embedUrl", e.target.value);
            }}
            isLoading={isEmbedContentLoading}
            validate={validate}
            defaultValue={embedUrl}
            data={item}
            isLoaded={!hasError && embedHtml}
            onBlur={e => onSetEmbedContent(e)}
            hasError={hasError}
            errorMessage={errorMessage}
            label={intl.formatMessage({ id: "projects.new.embed_video_audio.title" })}
            placeholderMessage="https://"
            noteMessage={intl.formatMessage({ id: "projects.new.embed_video_audio.hint" })}
          />
        </div>
      );
      break;
    case "text":
    default:
      contentItem = (
        <div data-label="project-text-content-item">
          <Form.SimpleBBCodeTextarea
            key={contentId || id}
            id={contentId || id}
            name={contentId || id}
            defaultValue={description}
            validate={validate}
            onChange={e => {
              onChange(contentId || id, "description", e.target.value);
            }}
            hasError={hasError}
            promptUrlMessage={intl.formatMessage({
              id: "projects.new.more_content_section.text.url_title",
            })}
            placeholderMessage={intl.formatMessage({ id: "projects.new.text.input.placeholder" })}
            errorMessage={errorMessage}
            autoFocus={!description}
          />
        </div>
      );
  }

  return contentItem;
};

FormContentItem.propTypes = {
  item: PropTypes.shape({
    humanizedItemType: PropTypes.string,
    id: PropTypes.string,
    caption: PropTypes.string,
    url: PropTypes.string,
    embedUrl: PropTypes.string,
    embedHtml: PropTypes.string,
    errors: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        messages: PropTypes.string,
      })
    ),
    contentId: PropTypes.string,
    description: PropTypes.string,
  }),
  properties: PropTypes.shape({
    validate: PropTypes.func,
    onChange: PropTypes.func,
    onImagesLoad: PropTypes.func,
    isImageLoading: PropTypes.bool,
    onSetEmbedContent: PropTypes.func,
    isEmbedContentLoading: PropTypes.bool,
  }),
  errors: PropTypes.array,
};

FormContentItem.defaultProps = {
  item: PropTypes.shape({
    humanizedItemType: "text",
    id: "item-text-01",
    caption: "",
    url: "",
    embedUrl: "",
    embedHtml: "",
    errors: [],
  }),
  properties: {
    validate: () => {},
    onChange: () => {},
    onImagesLoad: () => {},
    isImageLoading: () => {},
    onSetEmbedContent: () => {},
    isEmbedContentLoading: () => {},
  },
  errors: [],
};

export default FormContentItem;
